import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Calendar, 
  Users, 
  TrendingUp, 
  Clock,
  ArrowRight,
  CalendarDays,
  UserCheck,
  Loader2,
  Bell,
  CheckCircle,
  Timer,
  ChevronRight,
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { restaurant } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [trialInfo, setTrialInfo] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, bookingsRes, schedulerRes, billingRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/bookings`),
        axios.get(`${API}/admin/scheduler-status`).catch(() => ({ data: null })),
        axios.get(`${API}/admin/billing-status`).catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setRecentBookings(bookingsRes.data.bookings.slice(0, 5));
      setSchedulerStatus(schedulerRes.data);
      if (billingRes.data?.trial_info) setTrialInfo(billingRes.data.trial_info);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendReminders = async () => {
    setSendingReminders(true);
    try {
      const response = await axios.post(`${API}/admin/send-reminders`);
      toast.success(`${response.data.reminders_sent} reminder emails sent for ${response.data.date}`);
    } catch (error) {
      toast.error('Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      confirmed: 'bg-blue-100 text-blue-800',
      arrived: 'bg-amber-100 text-amber-800',
      seated: 'bg-emerald-100 text-emerald-800',
      completed: 'bg-slate-100 text-slate-800',
      cancelled: 'bg-red-100 text-red-800',
      'no-show': 'bg-gray-100 text-gray-800'
    };
    return styles[status] || styles.confirmed;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold">Dashboard</h1>
            <p className="text-muted-foreground mt-1">
              Welcome back! Here's what's happening at {restaurant?.name || 'your restaurant'}.
            </p>
          </div>
          <div className="flex gap-3">
            <Link to="/admin/bookings">
              <Button variant="outline" className="gap-2" data-testid="view-all-bookings">
                <CalendarDays className="h-4 w-4" />
                View All Bookings
              </Button>
            </Link>
          </div>
        </div>

        {/* Trial banner */}
        {trialInfo && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <Timer className="h-5 w-5 shrink-0 text-amber-600" />
              <div>
                <p className="text-sm font-semibold text-amber-900">
                  Free trial — Day {trialInfo.trial_day} of {trialInfo.trial_days_total}
                </p>
                <p className="text-xs text-amber-700 mt-0.5">
                  {trialInfo.days_remaining > 0
                    ? `${trialInfo.days_remaining} day${trialInfo.days_remaining !== 1 ? 's' : ''} remaining · No charge until your trial ends`
                    : 'Your trial has ended — subscribe to keep access'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <div className="hidden sm:flex h-2 w-32 rounded-full bg-amber-200 overflow-hidden">
                <div
                  className="h-full rounded-full bg-amber-500 transition-all"
                  style={{ width: `${Math.round((trialInfo.trial_day / trialInfo.trial_days_total) * 100)}%` }}
                />
              </div>
              <Link to="/admin/settings">
                <Button size="sm" variant="outline" className="border-amber-400 text-amber-800 hover:bg-amber-100 text-xs">
                  Upgrade now
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* Stats Grid — premium dark */}
        <div className="rounded-2xl bg-slate-950 p-1 shadow-2xl shadow-slate-900/40">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-white/10">
            {/* Today's Bookings — special card with guest count */}
            <div className="flex flex-col gap-1 p-6">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-widest text-amber-400/80">Today</p>
                <Calendar className="h-4 w-4 text-white/20" />
              </div>
              <p className="font-heading text-4xl font-bold text-white mt-1">{stats?.today_bookings ?? 0}</p>
              <p className="text-sm text-white/50">
                {stats?.today_bookings === 1 ? 'booking' : 'bookings'}
              </p>
              <div className="mt-3 flex items-center gap-2 rounded-lg bg-white/5 px-3 py-2">
                <Users className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                <span className="text-sm font-semibold text-white">{stats?.today_guests ?? 0}</span>
                <span className="text-xs text-white/50">expected guests</span>
              </div>
            </div>

            {/* Upcoming */}
            <div className="flex flex-col gap-1 p-6">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-widest text-white/40">Upcoming</p>
                <Clock className="h-4 w-4 text-white/20" />
              </div>
              <p className="font-heading text-4xl font-bold text-white mt-1">{stats?.upcoming_bookings ?? 0}</p>
              <p className="text-sm text-white/50">from today onward</p>
            </div>

            {/* Total Bookings */}
            <div className="flex flex-col gap-1 p-6">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-widest text-white/40">All Time</p>
                <TrendingUp className="h-4 w-4 text-white/20" />
              </div>
              <p className="font-heading text-4xl font-bold text-white mt-1">{stats?.total_bookings ?? 0}</p>
              <p className="text-sm text-white/50">total bookings</p>
            </div>

            {/* Total Customers */}
            <div className="flex flex-col gap-1 p-6">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-widest text-white/40">Guests</p>
                <Users className="h-4 w-4 text-white/20" />
              </div>
              <p className="font-heading text-4xl font-bold text-white mt-1">{stats?.total_customers ?? 0}</p>
              <p className="text-sm text-white/50">unique customers</p>
            </div>
          </div>
        </div>

        {/* Recent Bookings & Quick Actions */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Recent Bookings */}
          <Card className="lg:col-span-2 border-border/50">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading">Recent Bookings</CardTitle>
                <CardDescription>Latest reservations for your restaurant</CardDescription>
              </div>
              <Link to="/admin/bookings">
                <Button variant="ghost" size="sm" className="gap-1">
                  View All
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {recentBookings.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CalendarDays className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No bookings yet</p>
                  <p className="text-sm mt-1">Share your booking widget to start receiving reservations</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {recentBookings.map((booking) => (
                    <div
                      key={booking.id}
                      className="flex items-center justify-between p-4 rounded-lg bg-secondary/50 hover:bg-secondary transition-colors"
                      data-testid={`booking-${booking.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <UserCheck className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{booking.customer_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {format(new Date(booking.booking_date), 'MMM d')} at {booking.booking_time} • {booking.party_size} guests
                          </p>
                        </div>
                      </div>
                      <Badge className={getStatusBadge(booking.status)}>
                        {booking.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="font-heading">Quick Actions</CardTitle>
              <CardDescription>Common tasks and shortcuts</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Link to="/admin/bookings" className="block">
                <Button variant="outline" className="w-full justify-start gap-3" data-testid="quick-add-booking">
                  <Calendar className="h-4 w-4" />
                  Add New Booking
                </Button>
              </Link>
              <Link to="/tablet" className="block">
                <Button variant="outline" className="w-full justify-start gap-3" data-testid="quick-tablet-view">
                  <Clock className="h-4 w-4" />
                  Open Tablet View
                </Button>
              </Link>
              <Button 
                variant="outline" 
                className="w-full justify-start gap-3" 
                onClick={sendReminders}
                disabled={sendingReminders}
                data-testid="send-reminders"
              >
                {sendingReminders ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Bell className="h-4 w-4" />
                )}
                Send Tomorrow's Reminders
              </Button>
              <Link to="/admin/settings" className="block">
                <Button variant="outline" className="w-full justify-start gap-3" data-testid="quick-settings">
                  <Users className="h-4 w-4" />
                  Restaurant Settings
                </Button>
              </Link>
              
              {/* Automated Reminders Status */}
              {schedulerStatus && (
                <div className="pt-3 border-t border-border/50">
                  <div className="flex items-center gap-2 mb-2">
                    {schedulerStatus.scheduler_running ? (
                      <CheckCircle className="h-4 w-4 text-emerald-600" />
                    ) : (
                      <Timer className="h-4 w-4 text-amber-600" />
                    )}
                    <p className="text-xs font-medium">
                      Automated Reminders: {schedulerStatus.scheduler_running ? 'Active' : 'Inactive'}
                    </p>
                  </div>
                  {schedulerStatus.jobs?.map((job) => (
                    <p key={job.id} className="text-xs text-muted-foreground">
                      Next run: {job.next_run_time ? format(new Date(job.next_run_time), 'MMM d, h:mm a') : 'Not scheduled'}
                    </p>
                  ))}
                </div>
              )}
              
              {restaurant && (
                <div className="pt-3 border-t border-border/50">
                  <p className="text-xs text-muted-foreground mb-2">Your booking widget URL:</p>
                  <code className="text-xs bg-secondary p-2 rounded block break-all">
                    {process.env.REACT_APP_PUBLIC_URL || window.location.origin}/book/{restaurant.id}
                  </code>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
